import axios from 'axios';
import { RoleBadgeColors } from './roleBadge';

/**
 * Профиль текущего пользователя: только свои данные, пользователь определяется сервером по сессии (PROJECT_NOTES §35).
 * Запросы профиля и почты не передают логин - его берет сервер из сессии. Исключение - статус синхронизатора касс:
 * его эндпоинт пока работает по ККМ или логину из запроса (§35, открытые вопросы).
 */
const PROFILE_API = '/api/v2/profile';
const MAIL_API = '/api/v2/mail';
const SYNC_STATUS_API = '/api/v2/synchronizer/status';

export type TargetInterface = 'admin' | 'dashboard';

export interface ProfileData {
  userId: number;
  username: string;
  fullName: string;
  lastName: string;
  firstName: string;
  middleName: string;
  userphone: string;
  workPhone: string;
  workingHours: string;
  iin: string;
  pharmacyNum: string;
  kkm: string;
  cityId: number | string | null;
  regionName: string | null;
  belongsStorage: string | null;
  avatarUrl: string | null;
  passwordUpdatedAt: string | null;
  address: string;
  pharmacyAddress: string;
  latitude: string | null;
  longitude: string | null;
  accuracy: number | null;
  geoUpdatedAt: string | null;
  geoGuid: string | null;
  /** Микросервис геолокаций не ответил: точка неизвестна, а не отсутствует */
  geoUnavailable?: boolean;
}

/** Роль из БД: название и цвет бейджа - как в конструкторе ролей */
export interface ProfileRole {
  slug: string;
  name: string;
  level: number;
  targetInterface: TargetInterface;
  startPage: string;
  badge: RoleBadgeColors;
}

/**
 * Стартовая страница: current - что откроется после входа, personal - свой выбор (null - как у роли),
 * roleDefault - страница роли, available - ключи страниц, на которые у роли хватает прав (считает сервер)
 */
export interface ProfileStartPage {
  current: string;
  personal: string | null;
  roleDefault: string;
  available: string[];
}

export interface BiometricsState {
  hasBiometrics: boolean;
  registeredAt: string | null;
  updatedAt: string | null;
}

/**
 * Почта Carbonio. active - сессия действует; configured - логин и пароль сохранены;
 * unavailable - почтовый сервер не ответил, подключение не проверено
 */
export interface MailState {
  active: boolean;
  configured: boolean;
  unavailable: boolean;
  email: string;
  expiresAt: string | null;
  message: string | null;
}

export interface CompletenessItem {
  key: string;
  title: string;
  done: boolean;
}

/** Заполненность профиля: считает сервер, фронтенд только показывает */
export interface Completeness {
  done: number;
  total: number;
  items: CompletenessItem[];
  missing: string[];
  personalComplete: boolean;
}

export interface ProfileMe {
  profile: ProfileData;
  role: ProfileRole;
  startPage: ProfileStartPage;
  biometrics: BiometricsState;
  /** null - сервер до обновления: раньше он присылал почту только офису */
  mail: MailState | null;
  completeness: Completeness;
  impersonated: boolean;
}

export interface PersonalDataInput {
  lastName: string;
  firstName: string;
  middleName: string;
  userphone: string;
  workPhone: string;
  iin: string;
  workingHours: string;
}

export interface ActivityRow {
  id: number;
  createdAt: string;
  module: string;
  moduleTitle: string;
  section: string | null;
  sectionTitle: string | null;
  action: string;
  actionTitle: string;
  kind: string;
  kindTitle: string;
  result: 'success' | 'error' | 'denied' | string;
  resultTitle: string;
  entityLabel: string | null;
  pharmacyNum: string | null;
  period: string | null;
  message: string | null;
  summary: string | null;
  byAdministrator: boolean;
  ip: string | null;
  device: string;
}

export interface ActivityPage {
  rows: ActivityRow[];
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}

export interface ProfileSession {
  id: number;
  device: string;
  mobile: boolean;
  ip: string | null;
  currentSection: string | null;
  createdAt: string;
  lastActivityAt: string;
  isCurrent: boolean;
  isOnline: boolean;
  byAdministrator: boolean;
}

export interface MyPermission {
  code: string;
  name: string;
  description: string;
  hasScope: boolean;
  /** null - ранг 100, полный доступ */
  scope: string | null;
}

export interface MyPermissionModule {
  id: string;
  name: string;
  permissions: MyPermission[];
}

export interface MyPermissions {
  role: Omit<ProfileRole, 'startPage'>;
  unlimited: boolean;
  total: number;
  modules: MyPermissionModule[];
}

export interface NotificationEvent {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

export interface SyncStatus {
  isConfigured: boolean;
  isRunning: boolean;
  statusText: string;
  message?: string;
}

interface MessageResponse {
  success?: boolean;
  message?: string;
}

function messageOf(res: { data?: MessageResponse }, fallback: string): string {
  return (res.data && res.data.message) || fallback;
}

/** Сервер до обновления не присылает выбор страницы: тогда действует страница роли, выбор недоступен */
function parseStartPage(value: unknown, roleStartPage: string): ProfileStartPage {
  const data = value as Partial<ProfileStartPage> | null;
  if (!data || typeof data.current !== 'string' || !Array.isArray(data.available)) {
    return { current: roleStartPage, personal: null, roleDefault: roleStartPage, available: [] };
  }
  return {
    current: data.current,
    personal: typeof data.personal === 'string' ? data.personal : null,
    roleDefault: typeof data.roleDefault === 'string' ? data.roleDefault : roleStartPage,
    available: data.available.filter((key): key is string => typeof key === 'string')
  };
}

export async function fetchProfileMe(): Promise<ProfileMe> {
  const res = await axios.get(`${PROFILE_API}/me`);
  const data = res.data;
  if (!data || !data.success || !data.profile || !data.role || !data.completeness || !data.biometrics) {
    throw new Error('Сервер вернул профиль в неожиданном виде');
  }
  return {
    profile: data.profile,
    role: data.role,
    startPage: parseStartPage(data.startPage, data.role.startPage || 'profile'),
    biometrics: data.biometrics,
    mail: data.mail || null,
    completeness: data.completeness,
    impersonated: Boolean(data.impersonated)
  };
}

/** Имя и аватар для шапки панели */
export async function fetchProfileSummary(): Promise<{ fullName: string; avatarUrl: string | null }> {
  const res = await axios.get(`${PROFILE_API}/summary`);
  if (!res.data || !res.data.success) throw new Error('Сервер вернул профиль в неожиданном виде');
  return { fullName: res.data.fullName || '', avatarUrl: res.data.avatarUrl || null };
}

/** null - вернуть страницу роли */
export async function saveStartPage(startPage: string | null): Promise<{ message: string; startPage: ProfileStartPage }> {
  const res = await axios.put(`${PROFILE_API}/start-page`, { startPage });
  if (!res.data || !res.data.success || !res.data.startPage) throw new Error('Сервер не подтвердил сохранение стартовой страницы');
  return { message: messageOf(res, 'Стартовая страница сохранена'), startPage: parseStartPage(res.data.startPage, startPage || 'profile') };
}

export async function savePersonalData(input: PersonalDataInput): Promise<{ message: string; biometricsReset: boolean }> {
  const res = await axios.post(`${PROFILE_API}/personal-data`, input);
  return { message: messageOf(res, 'Персональные данные сохранены'), biometricsReset: Boolean(res.data && res.data.biometricsReset) };
}

export async function uploadAvatar(dataUrl: string): Promise<string> {
  const res = await axios.post(`${PROFILE_API}/avatar`, { avatar: dataUrl });
  if (!res.data || !res.data.success || !res.data.avatarUrl) throw new Error('Сервер не подтвердил сохранение аватара');
  return res.data.avatarUrl;
}

export async function deleteAvatar(): Promise<string> {
  const res = await axios.delete(`${PROFILE_API}/avatar`);
  return messageOf(res, 'Аватар удален');
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string; otherSessionsEnded: number }> {
  const res = await axios.post(`${PROFILE_API}/password`, { currentPassword, newPassword });
  return { message: messageOf(res, 'Пароль изменен'), otherSessionsEnded: Number(res.data && res.data.otherSessionsEnded) || 0 };
}

export async function fetchActivity(page: number, pageSize: number): Promise<ActivityPage> {
  const res = await axios.get(`${PROFILE_API}/activity`, { params: { page, pageSize } });
  if (!res.data || !res.data.success || !Array.isArray(res.data.rows)) {
    throw new Error('Сервер вернул журнал в неожиданном виде');
  }
  return {
    rows: res.data.rows,
    total: Number(res.data.total) || 0,
    page: Number(res.data.page) || page,
    pageSize: Number(res.data.pageSize) || pageSize,
    pages: Number(res.data.pages) || 1
  };
}

export async function fetchSessions(): Promise<ProfileSession[]> {
  const res = await axios.get(`${PROFILE_API}/sessions`);
  if (!res.data || !res.data.success || !Array.isArray(res.data.sessions)) {
    throw new Error('Сервер вернул список сессий в неожиданном виде');
  }
  return res.data.sessions;
}

export async function revokeSession(id: number): Promise<string> {
  const res = await axios.delete(`${PROFILE_API}/sessions/${id}`);
  return messageOf(res, 'Сессия завершена');
}

export async function revokeOtherSessions(): Promise<{ ended: number; message: string }> {
  const res = await axios.post(`${PROFILE_API}/sessions/revoke-others`);
  return { ended: Number(res.data && res.data.ended) || 0, message: messageOf(res, 'Сессии завершены') };
}

export async function fetchMyPermissions(): Promise<MyPermissions> {
  const res = await axios.get(`${PROFILE_API}/permissions`);
  if (!res.data || !res.data.success || !res.data.role || !Array.isArray(res.data.modules)) {
    throw new Error('Сервер вернул права в неожиданном виде');
  }
  return { role: res.data.role, unlimited: Boolean(res.data.unlimited), total: Number(res.data.total) || 0, modules: res.data.modules };
}

export async function fetchNotificationEvents(): Promise<NotificationEvent[]> {
  const res = await axios.get(`${PROFILE_API}/notifications`);
  if (!res.data || !res.data.success || !Array.isArray(res.data.events)) {
    throw new Error('Сервер вернул настройки уведомлений в неожиданном виде');
  }
  return res.data.events;
}

export async function saveNotificationEvents(events: Record<string, boolean>): Promise<string> {
  const res = await axios.put(`${PROFILE_API}/notifications`, { events });
  return messageOf(res, 'Настройки уведомлений сохранены');
}

export async function fetchMailState(): Promise<MailState> {
  const res = await axios.get(`${MAIL_API}/session`);
  if (!res.data || !res.data.success || !res.data.mail) throw new Error('Сервер вернул состояние почты в неожиданном виде');
  return res.data.mail;
}

export async function connectMail(mailLogin: string, mailPassword: string): Promise<{ message: string; mail: MailState }> {
  const res = await axios.post(`${MAIL_API}/auth`, { mailLogin, mailPassword });
  if (!res.data || !res.data.success || !res.data.mail) throw new Error('Сервер не подтвердил подключение почты');
  return { message: messageOf(res, 'Почта подключена'), mail: res.data.mail };
}

export async function disconnectMail(): Promise<string> {
  const res = await axios.delete(`${MAIL_API}/session`);
  return messageOf(res, 'Почта отключена');
}

/** Статус синхронизатора касс аптеки по ККМ профиля */
export async function fetchSyncStatus(kkm: string, username: string): Promise<SyncStatus> {
  const res = await axios.get(SYNC_STATUS_API, { params: kkm ? { kkm } : { username } });
  if (!res.data || !res.data.success) throw new Error('Сервер вернул статус синхронизатора в неожиданном виде');
  return {
    isConfigured: res.data.isConfigured !== false,
    isRunning: res.data.isRunning !== false,
    statusText: res.data.statusText || '',
    message: res.data.message || undefined
  };
}
